import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Badge, Panel, Typography } from "@/components/atoms";
import { useOperator } from "@/hooks";
import { useGetProviderSubscriptionsQuery } from "@/redux/apis/subscription/subscriptionPlansApi";
import { formatCurrency } from "@/utils";

export const SubscriptionPlanSection = () => {
  const operator = useOperator();
  const { data: subscriptions } = useGetProviderSubscriptionsQuery({
    providerId: operator.id,
    status: ["active", "pending_cancellation", "trial", "overdue"],
    limit: 1,
    orderBy: ["startDate:DESC"],
  });

  const activeSubscription = subscriptions?.[0];
  const scheduledChange = activeSubscription?.scheduledPlanChange;

  const navigate = useNavigate();

  const handleSelectPlan = () => {
    navigate("subscription-plan");
  };

  return (
    <Panel className="flex flex-col gap-4">
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center gap-2">
          <Typography variant="h2">Subscription Plan</Typography>
          {scheduledChange && <Badge type="info" message="Change Scheduled" size="sm" />}
        </div>
        <Typography variant="action" className="cursor-pointer text-info" onClick={handleSelectPlan}>
          Select a Plan
        </Typography>
      </div>

      {/* Data row */}
      <div className="grid grid-cols-4 gap-4">
        {/* Your Plan */}
        <div className="flex flex-col gap-1 text-neutral-dark-gray">
          <Typography variant="paragraph">Your Plan</Typography>
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              {activeSubscription?.subscription?.iconSvg && (
                <div
                  className="h-6 w-6 flex-shrink-0 [&>svg]:h-full [&>svg]:w-full"
                  dangerouslySetInnerHTML={{ __html: activeSubscription.subscription.iconSvg }}
                />
              )}
              <Typography variant="title">{activeSubscription?.subscription?.name ?? "-"}</Typography>
              {activeSubscription?.isTrialPeriod && activeSubscription?.trialEndsAt && (
                <Badge
                  type="info"
                  size="sm"
                  message={`Trial period ends ${format(new Date(activeSubscription.trialEndsAt), "dd MMM yyyy")}`}
                />
              )}
            </div>
            {scheduledChange && (
              <div className="flex items-center gap-1">
                <span className="text-sm text-info">→</span>
                <Typography variant="small" className="text-info">
                  {scheduledChange.newSubscriptionName}
                </Typography>
              </div>
            )}
          </div>
        </div>

        {/* Plan Cost */}
        <div className="flex flex-col gap-1 text-neutral-dark-gray">
          <Typography variant="paragraph">Plan Cost</Typography>
          <Typography variant="title">
            {activeSubscription?.subscription?.subscriptionFee != null
              ? formatCurrency(activeSubscription.subscription.subscriptionFee, 2, "AUD")
              : "-"}
          </Typography>
        </div>

        {/* Subscription ends at */}
        <div className="flex flex-col gap-1 text-neutral-dark-gray">
          <Typography variant="paragraph">Subscription ends at</Typography>
          <Typography variant="title">
            {activeSubscription?.endDate ? format(new Date(activeSubscription.endDate), "dd MMM yyyy") : "-"}
          </Typography>
        </div>

        {/* Subscription renews at / Change effective date */}
        <div className="flex flex-col gap-1 text-neutral-dark-gray">
          <Typography variant="paragraph">{scheduledChange ? "Change Effective" : "Subscription renews at"}</Typography>
          <Typography variant="title" className={scheduledChange ? "text-info" : undefined}>
            {scheduledChange
              ? format(new Date(scheduledChange.effectiveDate), "dd MMM yyyy")
              : activeSubscription?.billingCycleEnd
              ? format(new Date(activeSubscription.billingCycleEnd), "dd MMM yyyy")
              : "-"}
          </Typography>
        </div>
      </div>
    </Panel>
  );
};
